// Extra i18n strings for the FIND module + generic UI bits.
// Merged into the main translation dictionary at runtime.
export const FIND_TRANSLATIONS = {
    en: {
        find: {
            tabs: { leads: "Leads DB", prospect: "AI Prospecting", segments: "Segments", import: "Import" },
            header: {
                title: "Leads",
                subtitle: "Search, filter, reveal and push leads into your sequences.",
            },
            table: {
                empty: "No leads yet",
                emptyBody: "Seed 500 demo leads to explore or import your own CSV.",
                seed: "Seed 500 demo leads",
                seeding: "Seeding…",
                columns: {
                    lead: "Lead",
                    company: "Company",
                    location: "Location",
                    score: "Score",
                    tags: "Tags",
                    status: "Status",
                    updated: "Updated",
                },
                search: "Search name, company, title, industry…",
                filters: "Filters",
                clear: "Clear",
                sort: "Sort by",
                selected: (n) => `${n} selected`,
                bulkTag: "Add tag",
                bulkDelete: "Delete",
                bulkPushSequence: "Push to sequence",
                exportCsv: "Export CSV",
                page: "Page",
                of: "of",
                perPage: "per page",
                total: (n) => `${n} leads`,
                revealEmail: "Reveal email",
                revealPhone: "Reveal phone",
            },
            statuses: {
                new: "New", contacted: "Contacted", engaged: "Engaged",
                meeting: "Meeting", won: "Won", lost: "Lost",
            },
            seniorities: {
                entry: "Entry", mid: "Mid", senior: "Senior",
                executive: "Executive", "c-level": "C-Level",
            },
            filterGroups: {
                industries: "Industries",
                sizes: "Company size",
                countries: "Countries",
                seniorities: "Seniority",
                funding_stages: "Funding stage",
                statuses: "Status",
                tags: "Tags",
                min_score: "Min score",
            },
            detail: {
                back: "Back to leads",
                score: "AI score",
                breakdown: { fit: "Fit", intent: "Intent", engagement: "Engagement", timing: "Timing" },
                aiSummary: "AI summary",
                timeline: "Activity timeline",
                notes: "Notes",
                addNote: "Write a note…",
                addNoteBtn: "Add note",
                info: "Contact info",
                company: "Company",
                techStack: "Tech stack",
                revealEmail: "Reveal email",
                revealPhone: "Reveal phone",
                revealed: "Revealed",
                composeEmail: "Compose AI email",
                pushSequence: "Push to sequence",
                changeStatus: "Change status",
                deleted: "Lead deleted",
                deleteConfirm: "Delete this lead? This can't be undone.",
            },
            prospect: {
                title: "AI Prospecting",
                subtitle: "Describe your ideal customer in plain English. GPT-5.2 turns it into a real filter and matches against your leads DB.",
                placeholder: "e.g. Find CTOs at 20–100 SaaS in Berlin funded in the last 18 months",
                cta: "Run prospecting · 2 credits",
                running: "Thinking…",
                lastFilter: "Interpreted filter",
                matches: (n) => `${n} matching leads`,
                empty: "No matches for this query. Try broadening industries or sizes.",
                pushSequence: "Push all to sequence",
                createSegment: "Save as segment",
                notEnough: "Not enough credits. Top up in Billing.",
                examples: "Try these",
                examplesList: [
                    "Head of Growth at fintech scale-ups in France or UK",
                    "hot Series-A B2B SaaS CTOs in Germany or Netherlands",
                    "CMO at manufacturing 500+ people in Italy",
                    "VP Sales at cybersecurity 51-200 people in Nordics",
                ],
            },
            segments: {
                title: "Segments",
                subtitle: "Save reusable filters as segments to push into campaigns.",
                empty: "No segments yet",
                emptyBody: "Create your first segment from the leads table or the AI prospecting page.",
                create: "New segment",
                delete: "Delete",
                leads: (n) => `${n} leads`,
                nameLabel: "Segment name",
                namePh: "e.g. EU Fintech C-Level",
                combinator: "Combine with",
                addCondition: "Add condition",
                and: "AND", or: "OR",
                save: "Save segment",
                cancel: "Cancel",
                operators: {
                    equals: "equals", not_equals: "not equals", in: "is one of",
                    contains: "contains", gte: "≥", lte: "≤",
                },
                fields: {
                    industry: "Industry", company_size: "Company size", country: "Country",
                    seniority: "Seniority", funding_stage: "Funding stage",
                    status: "Status", score: "Score",
                },
            },
            importCsv: {
                title: "Import CSV",
                subtitle: "Upload a CSV of leads. Required columns: first_name, last_name, title, company_name.",
                optional: "Optional: company_domain, industry, company_size, country, city, email, phone, linkedin.",
                drop: "Drop your CSV here",
                or: "or",
                pick: "Pick a file",
                uploading: "Uploading…",
                imported: (n) => `${n} leads imported`,
                skipped: (n) => `${n} rows skipped`,
                errors: "Errors",
                downloadTemplate: "Download template",
                gotoLeads: "Go to leads",
            },
        },
    },
    fr: {
        find: {
            tabs: { leads: "Base de leads", prospect: "Prospection IA", segments: "Segments", import: "Import" },
            header: {
                title: "Leads",
                subtitle: "Cherche, filtre, révèle et pousse tes leads dans tes séquences.",
            },
            table: {
                empty: "Aucun lead pour l'instant",
                emptyBody: "Ajoute 500 leads démo pour explorer, ou importe ton propre CSV.",
                seed: "Ajouter 500 leads démo",
                seeding: "Chargement…",
                columns: {
                    lead: "Lead", company: "Société", location: "Localisation",
                    score: "Score", tags: "Tags", status: "Statut", updated: "MAJ",
                },
                search: "Chercher nom, société, poste, secteur…",
                filters: "Filtres",
                clear: "Effacer",
                sort: "Trier par",
                selected: (n) => `${n} sélectionné(s)`,
                bulkTag: "Ajouter tag",
                bulkDelete: "Supprimer",
                bulkPushSequence: "Vers une séquence",
                exportCsv: "Export CSV",
                page: "Page",
                of: "sur",
                perPage: "par page",
                total: (n) => `${n} leads`,
                revealEmail: "Révéler email",
                revealPhone: "Révéler tél.",
            },
            statuses: {
                new: "Nouveau", contacted: "Contacté", engaged: "Engagé",
                meeting: "RDV", won: "Gagné", lost: "Perdu",
            },
            seniorities: {
                entry: "Junior", mid: "Confirmé", senior: "Senior",
                executive: "Direction", "c-level": "C-Level",
            },
            filterGroups: {
                industries: "Secteurs",
                sizes: "Taille de société",
                countries: "Pays",
                seniorities: "Séniorité",
                funding_stages: "Levée de fonds",
                statuses: "Statut",
                tags: "Tags",
                min_score: "Score min.",
            },
            detail: {
                back: "Retour aux leads",
                score: "Score IA",
                breakdown: { fit: "Fit", intent: "Intent", engagement: "Engagement", timing: "Timing" },
                aiSummary: "Résumé IA",
                timeline: "Activité",
                notes: "Notes",
                addNote: "Écrire une note…",
                addNoteBtn: "Ajouter",
                info: "Contact",
                company: "Société",
                techStack: "Tech stack",
                revealEmail: "Révéler l'email",
                revealPhone: "Révéler le téléphone",
                revealed: "Révélé",
                composeEmail: "Rédiger un email IA",
                pushSequence: "Vers une séquence",
                changeStatus: "Changer statut",
                deleted: "Lead supprimé",
                deleteConfirm: "Supprimer ce lead ? Action irréversible.",
            },
            prospect: {
                title: "Prospection IA",
                subtitle: "Décris ton client idéal en langage naturel. GPT-5.2 le convertit en filtre réel et matche ta base.",
                placeholder: "ex. Trouve les CTO de SaaS 20-100 pers à Berlin financés depuis 18 mois",
                cta: "Lancer la prospection · 2 crédits",
                running: "L'IA réfléchit…",
                lastFilter: "Filtre interprété",
                matches: (n) => `${n} leads correspondants`,
                empty: "Aucun match. Élargis les secteurs ou tailles.",
                pushSequence: "Tout pousser en séquence",
                createSegment: "Sauver comme segment",
                notEnough: "Crédits insuffisants. Recharge dans Facturation.",
                examples: "Essaie ceux-ci",
                examplesList: [
                    "Head of Growth dans les fintech scale-up en France ou UK",
                    "CTO B2B SaaS Series-A en Allemagne ou Pays-Bas, hot leads",
                    "CMO industriel 500+ pers en Italie",
                    "VP Sales cybersécu 51-200 pers dans les pays nordiques",
                ],
            },
            segments: {
                title: "Segments",
                subtitle: "Sauve tes filtres favoris comme segments pour tes campagnes.",
                empty: "Aucun segment pour l'instant",
                emptyBody: "Crée ton premier segment depuis la table de leads ou la prospection IA.",
                create: "Nouveau segment",
                delete: "Supprimer",
                leads: (n) => `${n} leads`,
                nameLabel: "Nom du segment",
                namePh: "ex. Fintech EU C-Level",
                combinator: "Combiner avec",
                addCondition: "Ajouter une condition",
                and: "ET", or: "OU",
                save: "Sauver le segment",
                cancel: "Annuler",
                operators: {
                    equals: "égale", not_equals: "différent de", in: "parmi",
                    contains: "contient", gte: "≥", lte: "≤",
                },
                fields: {
                    industry: "Secteur", company_size: "Taille", country: "Pays",
                    seniority: "Séniorité", funding_stage: "Levée",
                    status: "Statut", score: "Score",
                },
            },
            importCsv: {
                title: "Import CSV",
                subtitle: "Uploade un CSV de leads. Colonnes requises : first_name, last_name, title, company_name.",
                optional: "Optionnel : company_domain, industry, company_size, country, city, email, phone, linkedin.",
                drop: "Dépose ton CSV ici",
                or: "ou",
                pick: "Choisir un fichier",
                uploading: "Envoi…",
                imported: (n) => `${n} leads importés`,
                skipped: (n) => `${n} lignes ignorées`,
                errors: "Erreurs",
                downloadTemplate: "Télécharger le template",
                gotoLeads: "Voir les leads",
            },
        },
    },
};
